import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { CAR } from '../utils/constants';

export type CarRole = 'driver' | 'cop';

export type DriverVisualVariant = {
  paintColor: number;
  accentColor: number;
};

type ReactiveCarLights = {
  brake: THREE.MeshBasicMaterial[];
  reverse: THREE.MeshBasicMaterial[];
  left: THREE.MeshBasicMaterial[];
  right: THREE.MeshBasicMaterial[];
};

const gltfLoader = new GLTFLoader();
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
gltfLoader.setDRACOLoader(dracoLoader);

const CAR_MODEL_CONFIGS: Record<string, { path: string; scale: number; rotationY: number; offsetY: number }> = {
  porsche: {
    path: '/assets/models/2022_porsche_911_gt3_touring_992.glb',
    scale: 1.12,
    rotationY: 0,
    offsetY: 0,
  },
  ferrari: {
    path: '/assets/models/ferrari_laferrari__www.vecarz.com.glb',
    scale: 1.12,
    rotationY: 0,
    offsetY: 0,
  },
  cop: {
    path: '/assets/models/police_car.glb',
    scale: 1.28,
    rotationY: 0,
    offsetY: 0,
  },
};

const loadedModels: Map<string, THREE.Group> = new Map();

/** Single shared load — every caller awaits the same promise (no empty-cache races). */
let carModelsLoadPromise: Promise<void> | null = null;

/** Available player car models */
const PLAYER_CAR_OPTIONS = ['porsche', 'ferrari'] as const;

/** Get a random player car model */
export function getRandomPlayerCar(): string {
  const index = Math.floor(Math.random() * PLAYER_CAR_OPTIONS.length);
  return PLAYER_CAR_OPTIONS[index]!;
}

/** Default player GLB when no `carType` is passed */
export const DEFAULT_DRIVER_CAR_MODEL = 'ferrari' as const;

function meshBoundsUnion(root: THREE.Object3D): THREE.Box3 {
  const box = new THREE.Box3();
  let has = false;
  root.updateWorldMatrix(true, true);
  root.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      const b = new THREE.Box3().setFromObject(child);
      if (!b.isEmpty()) {
        if (!has) {
          box.copy(b);
          has = true;
        } else {
          box.union(b);
        }
      }
    }
  });
  if (!has) box.setFromObject(root);
  return box;
}

export function preloadCarModels(): Promise<void> {
  if (carModelsLoadPromise) return carModelsLoadPromise;

  carModelsLoadPromise = (async () => {
    await Promise.all(
      Object.entries(CAR_MODEL_CONFIGS).map(async ([key, config]) => {
    try {
      const gltf = await gltfLoader.loadAsync(config.path);
      const model = gltf.scene;
      
      const box = meshBoundsUnion(model);
      const size = box.getSize(new THREE.Vector3());
      const targetLength = CAR.LENGTH;
      /** Longest horizontal span (car length is along X or Z in most GLBs). */
      const currentLength = Math.max(size.x, size.z, 1e-6);
      const autoScale = (targetLength / currentLength) * config.scale;
      
      model.scale.setScalar(autoScale);
      model.rotation.y = config.rotationY;
      
      const newBox = new THREE.Box3().setFromObject(model);
      const center = newBox.getCenter(new THREE.Vector3());
      model.position.y = -newBox.min.y + config.offsetY;
      model.position.x = -center.x;
      model.position.z = -center.z;
      
      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      
      loadedModels.set(key, model);
      console.log(`Loaded car model: ${key}`);
    } catch (error) {
      console.warn(`Failed to load car model ${key}:`, error);
    }
      }),
    );
  })();

  return carModelsLoadPromise;
}

/** Substrings that often appear on wheel-related nodes in GLBs (Ferrari, etc.). */
const WHEEL_NAME_POSITIVE = [
  'wheel',
  'tire',
  'tyre',
  'rim',
  'felge',
  'reifen',
  'alloy',
  'disc',
  'rotor',
];

/** Exclude steering wheel, mirrors, lights, etc. */
function nameLooksLikeWheelPart(name: string): boolean {
  const n = name.toLowerCase();
  if (
    n.includes('caliper') ||
    n.includes('steering') ||
    n.includes('mirror') ||
    n.includes('wiper') ||
    n.includes('light') ||
    n.includes('lamp') ||
    n.includes('glass') ||
    n.includes('window') ||
    n.includes('logo') ||
    n.includes('badge') ||
    n.includes('interior')
  ) {
    return false;
  }
  if (n.includes('brake') && (n.includes('light') || n.includes('lamp'))) return false;
  return WHEEL_NAME_POSITIVE.some((k) => n.includes(k));
}

/**
 * Ferrari LaFerrari: groups `tire 1`…`tire 4` with children (`tire 1_rim`, tread, …).
 * Highest matching node per branch (child skipped if parent matches).
 */
function collectWheelMeshesByName(root: THREE.Object3D): THREE.Object3D[] {
  const wheels: THREE.Object3D[] = [];
  root.traverse((child) => {
    if (!nameLooksLikeWheelPart(child.name)) return;
    let p: THREE.Object3D | null = child.parent;
    while (p && p !== root) {
      if (nameLooksLikeWheelPart(p.name)) return;
      p = p.parent;
    }
    wheels.push(child);
  });
  return wheels;
}

/** Generic GLB mesh names — reject body / lighting (quadrant picking wrongly grabbed headlights, tailgate). */
function nameExcludesWheelGeometryCandidate(name: string): boolean {
  const n = name.toLowerCase();
  return (
    n.includes('glass') ||
    n.includes('light') ||
    n.includes('lamp') ||
    n.includes('headlight') ||
    n.includes('fog') ||
    n.includes('drl') ||
    n.includes('tail') ||
    n.includes('gate') ||
    n.includes('trunk') ||
    n.includes('logo') ||
    n.includes('mirror') ||
    n.includes('window') ||
    n.includes('windshield') ||
    n.includes('lens') ||
    n.includes('grille') ||
    n.includes('spoiler') ||
    n.includes('wiper') ||
    n.includes('antenna') ||
    n.includes('exhaust') ||
    n.includes('muffler') ||
    n.includes('camera') ||
    n.includes('indicator') ||
    n.includes('signal') ||
    n.includes('flare') ||
    n.includes('roof') ||
    n.includes('hood') ||
    n.includes('bumper') ||
    n.includes('splitter') ||
    n.includes('diffuser') ||
    n.includes('interior') ||
    n.includes('seat') ||
    n.includes('steering')
  );
}

/**
 * Tire-sized cylinders **low** on the car and **outboard** (tailgate/exhaust often sit on centerline).
 */
function collectWheelMeshesGeometryFallback(root: THREE.Object3D): THREE.Object3D[] {
  const carBox = new THREE.Box3().setFromObject(root);
  if (carBox.isEmpty()) return [];

  const carMinY = carBox.min.y;
  const carSize = carBox.getSize(new THREE.Vector3());
  const carHeight = Math.max(carSize.y, 0.01);
  const carWidth = Math.max(carSize.x, 0.01);
  /** Wheel centers sit just above the road; lights/tail are higher — stay in bottom ~27% of body. */
  const maxWheelCenterY = carMinY + carHeight * 0.27;
  /** Track is wide — reject centerline meshes (tailgate, single exhaust, license plate). */
  const minLateralFromCenter = carWidth * 0.17;

  const candidates: { obj: THREE.Object3D; score: number }[] = [];
  const tmp = new THREE.Vector3();

  root.updateMatrixWorld(true);
  root.traverse((child) => {
    if (!(child as THREE.Mesh).isMesh) return;
    const mesh = child as THREE.Mesh;
    const name = mesh.name;
    if (nameExcludesWheelGeometryCandidate(name)) return;

    const box = new THREE.Box3().setFromObject(mesh);
    if (box.isEmpty()) return;
    const size = box.getSize(tmp);
    const max = Math.max(size.x, size.y, size.z);
    const min = Math.min(size.x, size.y, size.z);
    if (max < 0.2 || max > 1.25 || min < 0.05 || min > 0.52) return;
    if (max / min < 1.45 || max / min > 5.5) return;

    const center = box.getCenter(new THREE.Vector3());
    if (center.y > maxWheelCenterY) return;

    const cx = (carBox.min.x + carBox.max.x) * 0.5;
    if (Math.abs(center.x - cx) < minLateralFromCenter) return;

    const volume = size.x * size.y * size.z;
    if (volume < 0.004 || volume > 0.48) return;

    const ratio = max / min;
    const score = volume * Math.min(ratio / 3, 1);
    candidates.push({ obj: mesh, score });
  });

  candidates.sort((a, b) => b.score - a.score);
  const out: THREE.Object3D[] = [];
  for (const { obj } of candidates) {
    if (out.length >= 4) break;
    out.push(obj);
  }

  return out;
}

/** First mesh geometry under a wheel (or the wheel itself if it is a Mesh). */
function firstMeshGeometry(root: THREE.Object3D): THREE.BufferGeometry | null {
  if ((root as THREE.Mesh).isMesh) {
    const m = root as THREE.Mesh;
    return m.geometry ?? null;
  }
  let g: THREE.BufferGeometry | null = null;
  root.traverse((c) => {
    if (g) return;
    if ((c as THREE.Mesh).isMesh && (c as THREE.Mesh).geometry) {
      g = (c as THREE.Mesh).geometry;
    }
  });
  return g;
}

/**
 * Roll axis for wheel spin — never `y` (steering uses local Y).
 * Smallest bbox extent is tire width along the axle → rotate around that axis (x or z).
 */
function inferRollAxisFromWheel(wheel: THREE.Object3D): 'x' | 'z' {
  let sx = 1;
  let sy = 1;
  let sz = 1;
  const geom = firstMeshGeometry(wheel);
  if (geom) {
    /** After geometry.translate, existing bbox can be stale. */
    geom.computeBoundingBox();
    if (geom.boundingBox) {
      const s = geom.boundingBox.getSize(new THREE.Vector3());
      if (s.x + s.y + s.z > 1e-6) {
        sx = s.x;
        sy = s.y;
        sz = s.z;
      }
    }
  } else {
    const box = new THREE.Box3().setFromObject(wheel);
    if (!box.isEmpty()) {
      const s = box.getSize(new THREE.Vector3());
      sx = s.x;
      sy = s.y;
      sz = s.z;
    }
  }

  const min = Math.min(sx, sy, sz);
  if (min === sx) return 'x';
  if (min === sz) return 'z';
  return sx <= sz ? 'x' : 'z';
}

function tagRollAxisForGlbWheels(wheels: THREE.Object3D[]): void {
  for (const w of wheels) {
    w.userData.rollAxis = inferRollAxisFromWheel(w);
  }
}

function addReactiveLightKit(group: THREE.Group): void {
  const W = CAR.WIDTH;
  const H = CAR.HEIGHT;
  const L = CAR.LENGTH;
  const lights: ReactiveCarLights = {
    brake: [],
    reverse: [],
    left: [],
    right: [],
  };

  function lightPlane(
    color: number,
    x: number,
    y: number,
    z: number,
    w: number,
    h: number,
    target: keyof ReactiveCarLights,
    rear = false,
  ): void {
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    mesh.position.set(x, y, z);
    if (rear) mesh.rotation.y = Math.PI;
    mesh.renderOrder = 3;
    group.add(mesh);
    lights[target].push(mat);
  }

  const rearZ = -L * 0.512;
  const frontZ = L * 0.512;
  const lampY = H * 0.02;
  lightPlane(0xff1f2f, -W * 0.34, lampY, rearZ, 0.34, 0.16, 'brake', true);
  lightPlane(0xff1f2f, W * 0.34, lampY, rearZ, 0.34, 0.16, 'brake', true);
  lightPlane(0xeef8ff, -W * 0.12, lampY - 0.03, rearZ - 0.002, 0.16, 0.1, 'reverse', true);
  lightPlane(0xeef8ff, W * 0.12, lampY - 0.03, rearZ - 0.002, 0.16, 0.1, 'reverse', true);
  lightPlane(0xffa51f, -W * 0.53, lampY + 0.02, rearZ - 0.004, 0.13, 0.13, 'left', true);
  lightPlane(0xffa51f, W * 0.53, lampY + 0.02, rearZ - 0.004, 0.13, 0.13, 'right', true);
  lightPlane(0xffb12a, -W * 0.47, lampY + 0.02, frontZ, 0.13, 0.13, 'left');
  lightPlane(0xffb12a, W * 0.47, lampY + 0.02, frontZ, 0.13, 0.13, 'right');

  group.userData.reactiveLights = lights;
}

/**
 * Porsche 992 GT3 Touring: Try multiple naming patterns for wheel detection.
 * Different exports may use different naming conventions.
 */
function collectPorscheWheels(root: THREE.Object3D): THREE.Object3D[] {
  const out: THREE.Object3D[] = [];
  
  // Try Combined3DWheel_ pattern first (Forza-style)
  root.traverse((child) => {
    if (child.name.startsWith('Combined3DWheel_')) out.push(child);
  });
  if (out.length >= 4) return out;
  
  // Try Wheel patterns
  out.length = 0;
  root.traverse((child) => {
    const name = child.name.toLowerCase();
    if (name.includes('wheel') && !name.includes('steering')) {
      // Prefer parent nodes that contain wheel geometry
      if (child.children.length > 0 || (child as THREE.Mesh).isMesh) {
        out.push(child);
      }
    }
  });
  if (out.length >= 4) return out.slice(0, 4);
  
  // Try rim/tire patterns
  out.length = 0;
  const wheelGroups = new Map<string, THREE.Object3D>();
  root.traverse((child) => {
    const name = child.name.toLowerCase();
    // Look for FL, FR, RL, RR patterns
    const match = name.match(/(fl|fr|rl|rr|front.*left|front.*right|rear.*left|rear.*right)/i);
    if (match && (name.includes('rim') || name.includes('tire') || name.includes('wheel'))) {
      const key = match[1]!.toLowerCase().replace(/[^a-z]/g, '').slice(0, 2);
      if (!wheelGroups.has(key)) {
        // Find the parent that contains this wheel part
        let parent = child.parent;
        while (parent && parent.name.toLowerCase().includes(key)) {
          child = parent;
          parent = parent.parent;
        }
        wheelGroups.set(key, child);
      }
    }
  });
  
  return Array.from(wheelGroups.values());
}

/** Resolve wheel objects for GLB cars: named meshes first, then geometry heuristic. */
function resolveWheelMeshesForGlb(clone: THREE.Object3D, modelKey: string): THREE.Object3D[] {
  if (modelKey === 'porsche') {
    const porscheWheels = collectPorscheWheels(clone);
    if (porscheWheels.length >= 4) {
      console.log('Porsche wheels found:', porscheWheels.map(w => w.name));
      return porscheWheels;
    }
  }

  const byName = collectWheelMeshesByName(clone);
  /** Prefer any name-based hit (1–3 wheels) over geometry picking 4 random tire-sized meshes. */
  if (byName.length > 0) return byName;

  const byGeom = collectWheelMeshesGeometryFallback(clone);
  if (byGeom.length >= 4) return byGeom;

  return byGeom;
}

export function createRealCar(role: CarRole = 'driver', carType?: string): THREE.Group {
  const group = new THREE.Group();
  
  let modelKey = carType;
  if (!modelKey) {
    modelKey = role === 'cop' ? 'cop' : DEFAULT_DRIVER_CAR_MODEL;
  }
  
  const cachedModel = loadedModels.get(modelKey!);
  if (cachedModel) {
    const clone = cachedModel.clone();
    clone.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        if (mesh.material) {
          mesh.material = (mesh.material as THREE.Material).clone();
        }
      }
    });

    group.userData.role = role;
    group.userData.modelType = modelKey;
    group.userData.isRealModel = true;

    const wheels = resolveWheelMeshesForGlb(clone, modelKey!);
    if (wheels.length > 0) {
      tagRollAxisForGlbWheels(wheels);
    }

    group.add(clone);
    if (wheels.length > 0) {
      group.userData.wheels = wheels;
      group.userData.wheelRotation = 0;
    }
    addReactiveLightKit(group);

    return group;
  }
  
  const fallback = createCar(role);
  fallback.userData.isRealModel = false;
  addReactiveLightKit(fallback);
  return fallback;
}

function materialHasPaintColor(
  material: THREE.Material,
): material is THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial {
  return 'color' in material && material.color instanceof THREE.Color;
}

function isTintablePaintMesh(mesh: THREE.Mesh): boolean {
  const name = mesh.name.toLowerCase();
  return !(
    name.includes('glass') ||
    name.includes('window') ||
    name.includes('light') ||
    name.includes('lamp') ||
    name.includes('wheel') ||
    name.includes('tire') ||
    name.includes('tyre') ||
    name.includes('rim') ||
    name.includes('interior') ||
    name.includes('seat') ||
    name.includes('logo') ||
    name.includes('badge')
  );
}

function addLoadoutAccentKit(group: THREE.Group, accentColor: number): void {
  const old = group.getObjectByName('driver-loadout-accent-kit');
  if (old) old.removeFromParent();

  const kit = new THREE.Group();
  kit.name = 'driver-loadout-accent-kit';
  const mat = new THREE.MeshBasicMaterial({
    color: accentColor,
    transparent: true,
    opacity: 0.72,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  const stripGeo = new THREE.BoxGeometry(0.035, 0.035, CAR.LENGTH * 0.62);
  for (const side of [-1, 1]) {
    const strip = new THREE.Mesh(stripGeo, mat.clone());
    strip.position.set(side * (CAR.WIDTH * 0.56), CAR.HEIGHT * 0.28, 0);
    kit.add(strip);
  }

  const hoodGeo = new THREE.BoxGeometry(CAR.WIDTH * 0.5, 0.03, 0.055);
  const hood = new THREE.Mesh(hoodGeo, mat.clone());
  hood.position.set(0, CAR.HEIGHT * 0.66, CAR.LENGTH * 0.23);
  kit.add(hood);

  group.add(kit);
}

export function applyDriverVisualVariant(
  car: THREE.Group,
  variant: DriverVisualVariant,
): void {
  const paint = new THREE.Color(variant.paintColor);
  const paintMeshes = car.userData.paintMeshes as THREE.Mesh[] | undefined;
  const tintMesh = (mesh: THREE.Mesh, strength: number): void => {
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      if (!materialHasPaintColor(material)) continue;
      material.color.lerp(paint, strength);
      material.needsUpdate = true;
    }
  };

  if (paintMeshes && paintMeshes.length > 0) {
    paintMeshes.forEach((mesh) => tintMesh(mesh, 0.82));
  } else {
    car.traverse((child) => {
      if (!(child as THREE.Mesh).isMesh) return;
      const mesh = child as THREE.Mesh;
      if (isTintablePaintMesh(mesh)) tintMesh(mesh, 0.46);
    });
  }

  addLoadoutAccentKit(car, variant.accentColor);
}

const DRIVER_PAINT = 0x0d4a5c;
const DRIVER_ACCENT = 0x00d4e8;
const COP_PAINT = 0x6a1a28;
const COP_ACCENT = 0xff1a3a;

const W_SEG = 28;

function paintBody(
  color: number,
  metal: number,
  rough: number,
): THREE.MeshPhysicalMaterial {
  const c = new THREE.Color(color);
  return new THREE.MeshPhysicalMaterial({
    color,
    metalness: metal,
    roughness: rough,
    clearcoat: 1,
    clearcoatRoughness: 0.12,
    envMapIntensity: 1.35,
    sheen: 0.55,
    sheenRoughness: 0.38,
    sheenColor: c.clone().multiplyScalar(1.08),
  });
}

function chrome(): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: 0xc8d0dc,
    metalness: 0.92,
    roughness: 0.18,
    envMapIntensity: 1.35,
  });
}

function rubberBlack(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x080808,
    metalness: 0.04,
    roughness: 0.96,
  });
}

function glassWindshield(): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: 0x0a1620,
    metalness: 0.02,
    roughness: 0.04,
    transmission: 0.88,
    thickness: 0.45,
    ior: 1.48,
    transparent: true,
    opacity: 1,
    side: THREE.DoubleSide,
    envMapIntensity: 1,
  });
}

function glassSideTint(): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: 0x050a12,
    metalness: 0.15,
    roughness: 0.12,
    transmission: 0.35,
    thickness: 0.2,
    ior: 1.45,
    transparent: true,
    opacity: 0.92,
    side: THREE.DoubleSide,
    envMapIntensity: 0.85,
  });
}

function grilleMat(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x080a0e,
    metalness: 0.35,
    roughness: 0.65,
  });
}

/**
 * Sport wheel: tire + multi-spoke rim + brake disc hint.
 */
function wheel(wx: number, wz: number, accent: number): THREE.Group {
  const g = new THREE.Group();
  g.position.set(wx, 0.34, wz);

  const tireOut = 0.43;
  const tireIn = 0.41;
  const tireW = 0.3;
  const tire = new THREE.Mesh(
    new THREE.CylinderGeometry(tireOut, tireIn, tireW, W_SEG),
    rubberBlack(),
  );
  tire.rotation.z = Math.PI / 2;
  tire.castShadow = true;
  g.add(tire);

  const rim = new THREE.Mesh(
    new THREE.CylinderGeometry(0.28, 0.29, 0.26, W_SEG),
    new THREE.MeshPhysicalMaterial({
      color: 0xd0d6e0,
      metalness: 0.88,
      roughness: 0.18,
      envMapIntensity: 1.1,
    }),
  );
  rim.rotation.z = Math.PI / 2;
  rim.castShadow = true;
  g.add(rim);

  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.2, 0.04, 16),
    new THREE.MeshStandardMaterial({
      color: 0x3a3a3a,
      metalness: 0.55,
      roughness: 0.42,
    }),
  );
  disc.rotation.z = Math.PI / 2;
  disc.position.x = wx > 0 ? -0.02 : 0.02;
  g.add(disc);

  const spokeCount = 5;
  for (let i = 0; i < spokeCount; i++) {
    const a = (i / spokeCount) * Math.PI * 2;
    const sp = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.22, 0.04),
      new THREE.MeshPhysicalMaterial({
        color: 0xa8b0c0,
        metalness: 0.75,
        roughness: 0.25,
        envMapIntensity: 1,
      }),
    );
    sp.rotation.z = Math.PI / 2;
    sp.rotation.y = a;
    sp.position.set(Math.sin(a) * 0.14, 0, Math.cos(a) * 0.14);
    g.add(sp);
  }

  const caliper = new THREE.Mesh(
    new THREE.BoxGeometry(0.07, 0.26, 0.14),
    new THREE.MeshStandardMaterial({
      color: accent,
      emissive: accent,
      emissiveIntensity: 0.18,
      metalness: 0.5,
      roughness: 0.4,
    }),
  );
  caliper.rotation.z = Math.PI / 2;
  g.add(caliper);

  return g;
}

function mirrorMesh(side: 1 | -1, W: number, H: number, L: number): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(0.12, 0.1, 0.06),
    chrome(),
  );
  m.position.set(side * (W * 0.52), H * 0.26, L * 0.12);
  m.castShadow = true;
  return m;
}

/**
 * prd2: low, wide silhouette — physical paint, glass, chrome, detailed wheels.
 */
export function createCar(role: CarRole = 'driver'): THREE.Group {
  const group = new THREE.Group();
  const accent = role === 'driver' ? DRIVER_ACCENT : COP_ACCENT;
  const bodyColor = role === 'driver' ? DRIVER_PAINT : COP_PAINT;

  const W = CAR.WIDTH;
  const H = CAR.HEIGHT;
  const L = CAR.LENGTH;
  const rad = 0.12;

  const lower = new THREE.Mesh(
    new RoundedBoxGeometry(W * 1.06, H * 0.52, L * 0.98, 4, rad),
    paintBody(bodyColor, 0.62, 0.28),
  );
  lower.position.y = -H * 0.06;
  lower.castShadow = true;
  lower.receiveShadow = true;
  group.add(lower);

  const cabin = new THREE.Mesh(
    new RoundedBoxGeometry(W * 0.88, H * 0.48, L * 0.42, 3, rad * 0.85),
    paintBody(
      role === 'driver' ? 0x0a3c4a : 0x5a1522,
      0.58,
      0.3,
    ),
  );
  cabin.position.set(0, H * 0.28, -L * 0.04);
  cabin.castShadow = true;
  group.add(cabin);

  const roof = new THREE.Mesh(
    new RoundedBoxGeometry(W * 0.78, H * 0.22, L * 0.36, 2, rad * 0.6),
    paintBody(bodyColor, 0.65, 0.26),
  );
  roof.position.set(0, H * 0.48, -L * 0.02);
  roof.castShadow = true;
  group.add(roof);

  const wind = new THREE.Mesh(
    new THREE.BoxGeometry(W * 0.82, H * 0.36, 0.05),
    glassWindshield(),
  );
  wind.position.set(0, H * 0.3, L * 0.12);
  wind.rotation.x = -0.38;
  wind.castShadow = false;
  group.add(wind);

  const sideL = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, H * 0.32, L * 0.32),
    glassSideTint(),
  );
  sideL.position.set(-W * 0.46, H * 0.26, -L * 0.02);
  group.add(sideL);
  const sideR = sideL.clone();
  sideR.position.x = W * 0.46;
  group.add(sideR);

  const hood = new THREE.Mesh(
    new RoundedBoxGeometry(W * 1.08, H * 0.3, L * 0.44, 3, rad * 0.9),
    paintBody(bodyColor, 0.68, 0.22),
  );
  hood.position.set(0, H * 0.08, L * 0.28);
  hood.castShadow = true;
  group.add(hood);

  const hoodLine = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, H * 0.08, L * 0.38),
    new THREE.MeshStandardMaterial({
      color: 0x000000,
      metalness: 0.2,
      roughness: 0.85,
      opacity: 0.35,
      transparent: true,
    }),
  );
  hoodLine.position.set(0, H * 0.12, L * 0.28);
  group.add(hoodLine);

  const grille = new THREE.Mesh(
    new THREE.PlaneGeometry(W * 0.72, H * 0.2),
    grilleMat(),
  );
  grille.position.set(0, -H * 0.18, L * 0.495);
  group.add(grille);

  const lip = new THREE.Mesh(
    new THREE.BoxGeometry(W * 1.12, 0.04, L * 0.08),
    chrome(),
  );
  lip.position.set(0, -H * 0.32, L * 0.46);
  group.add(lip);

  const bumperF = new THREE.Mesh(
    new RoundedBoxGeometry(W * 1.12, H * 0.2, L * 0.12, 2, rad * 0.45),
    new THREE.MeshPhysicalMaterial({
      color: 0x0c1018,
      metalness: 0.55,
      roughness: 0.38,
      clearcoat: 0.6,
      clearcoatRoughness: 0.4,
      envMapIntensity: 0.9,
    }),
  );
  bumperF.position.set(0, -H * 0.3, L * 0.48);
  bumperF.castShadow = true;
  group.add(bumperF);

  const bumperR = new THREE.Mesh(
    new RoundedBoxGeometry(W * 1.04, H * 0.18, L * 0.14, 2, rad * 0.45),
    new THREE.MeshPhysicalMaterial({
      color: 0x101820,
      metalness: 0.52,
      roughness: 0.42,
      clearcoat: 0.5,
      clearcoatRoughness: 0.45,
      envMapIntensity: 0.85,
    }),
  );
  bumperR.position.set(0, -H * 0.32, -L * 0.48);
  group.add(bumperR);

  const skirt = new THREE.Mesh(
    new THREE.BoxGeometry(W * 1.16, 0.055, L * 0.9),
    new THREE.MeshStandardMaterial({
      color: accent,
      emissive: accent,
      emissiveIntensity: 0.22,
      metalness: 0.4,
      roughness: 0.38,
    }),
  );
  skirt.position.y = -H * 0.44;
  group.add(skirt);

  const sillL = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.04, L * 0.85),
    chrome(),
  );
  sillL.position.set(-W * 0.54, -H * 0.38, 0);
  group.add(sillL);
  const sillR = sillL.clone();
  sillR.position.x = W * 0.54;
  group.add(sillR);

  const exL = new THREE.Mesh(
    new THREE.CylinderGeometry(0.075, 0.095, 0.32, 10),
    new THREE.MeshPhysicalMaterial({
      color: 0x1a1c20,
      metalness: 0.75,
      roughness: 0.28,
      envMapIntensity: 0.8,
    }),
  );
  exL.rotation.z = Math.PI / 2;
  exL.position.set(-W * 0.38, -H * 0.34, -L * 0.4);
  group.add(exL);
  const exR = exL.clone();
  exR.position.x = W * 0.38;
  group.add(exR);

  group.add(mirrorMesh(-1, W, H, L));
  group.add(mirrorMesh(1, W, H, L));

  if (role === 'driver') {
    const wing = new THREE.Mesh(
      new THREE.BoxGeometry(W * 1.08, 0.07, L * 0.2),
      new THREE.MeshPhysicalMaterial({
        color: 0x121418,
        metalness: 0.65,
        roughness: 0.32,
        clearcoat: 0.4,
        clearcoatRoughness: 0.35,
        envMapIntensity: 0.85,
      }),
    );
    wing.position.set(0, H * 0.42, -L * 0.4);
    wing.castShadow = true;
    group.add(wing);
    const uprights = new THREE.Mesh(
      new THREE.BoxGeometry(W * 0.82, 0.1, 0.055),
      paintBody(bodyColor, 0.45, 0.36),
    );
    uprights.position.set(0, H * 0.34, -L * 0.36);
    group.add(uprights);
  } else {
    const bar = new THREE.Mesh(
      new THREE.BoxGeometry(W * 0.92, 0.12, 0.26),
      new THREE.MeshStandardMaterial({
        color: 0xf0f4f8,
        emissive: 0xffffff,
        emissiveIntensity: 0.65,
        metalness: 0.2,
        roughness: 0.35,
      }),
    );
    bar.position.set(0, H * 0.46, -0.04);
    group.add(bar);
    const b1 = new THREE.Mesh(
      new THREE.BoxGeometry(0.11, 0.09, 0.3),
      new THREE.MeshStandardMaterial({
        color: 0x2244ff,
        emissive: 0x4466ff,
        emissiveIntensity: 0.9,
      }),
    );
    b1.position.set(-0.21, H * 0.46, -0.04);
    group.add(b1);
    const b2 = new THREE.Mesh(
      new THREE.BoxGeometry(0.11, 0.09, 0.3),
      new THREE.MeshStandardMaterial({
        color: 0xff2222,
        emissive: 0xff4444,
        emissiveIntensity: 0.9,
      }),
    );
    b2.position.set(0.21, H * 0.46, -0.04);
    group.add(b2);
  }

  const wz = L * 0.34;
  const wx = W * 0.48;
  const wheelFL = wheel(-wx, wz, accent);
  const wheelFR = wheel(wx, wz, accent);
  const wheelRL = wheel(-wx, -wz, accent);
  const wheelRR = wheel(wx, -wz, accent);
  wheelFL.name = 'wheel_fl';
  wheelFR.name = 'wheel_fr';
  wheelRL.name = 'wheel_rl';
  wheelRR.name = 'wheel_rr';
  group.add(wheelFL);
  group.add(wheelFR);
  group.add(wheelRL);
  group.add(wheelRR);
  group.userData.wheels = [wheelFL, wheelFR, wheelRL, wheelRR];

  // Headlight glow meshes instead of SpotLights (no real lights = big perf win)
  const headlightMat = new THREE.MeshBasicMaterial({
    color: 0xfff5f0,
    transparent: true,
    opacity: 0.7,
  });
  for (const lx of [-W * 0.3, W * 0.3]) {
    const glow = new THREE.Mesh(new THREE.CircleGeometry(0.2, 6), headlightMat);
    glow.position.set(lx, 0.16, L * 0.48);
    group.add(glow);
  }

  const hlMat = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 1.1,
    metalness: 0.1,
    roughness: 0.15,
    clearcoat: 0.8,
    clearcoatRoughness: 0.1,
  });
  const hl = new THREE.Mesh(new THREE.PlaneGeometry(0.38, 0.24), hlMat);
  hl.position.set(-W * 0.3, 0.06, L * 0.492);
  group.add(hl);
  const hl2 = hl.clone();
  hl2.position.x = W * 0.3;
  group.add(hl2);

  const tailMat = new THREE.MeshPhysicalMaterial({
    color: accent,
    emissive: accent,
    emissiveIntensity: 0.75,
    metalness: 0.15,
    roughness: 0.35,
  });
  const tail = new THREE.Mesh(
    new THREE.PlaneGeometry(W * 0.34, H * 0.28),
    tailMat,
  );
  tail.position.set(0, 0.05, -L * 0.505);
  tail.rotation.y = Math.PI;
  group.add(tail);

  group.userData.role = role;
  /** Meshes that receive roughness / wear from collisions (GameView). */
  group.userData.paintMeshes = [lower, cabin, roof, hood, bumperF, bumperR];
  group.userData.wheelRotation = 0;
  return group;
}

/**
 * Update wheel spin based on vehicle speed.
 * Rotates wheels around their tagged roll axis (X by default, or custom axis from tagRollAxisForGlbWheels).
 * NOTE: Porsche wheel animation is skipped due to model issues.
 */
export function updateCarWheels(
  car: THREE.Group,
  speed: number,
  dt: number,
  steer: number = 0,
): void {
  // Skip wheel animation for Porsche - the model has issues
  const modelType = car.userData.modelType as string | undefined;
  if (modelType === 'porsche') return;

  const wheels = car.userData.wheels as THREE.Object3D[] | undefined;
  if (!wheels || wheels.length === 0) return;

  const wheelRadius = 0.35;
  const rotationDelta = (speed / wheelRadius) * dt;
  
  car.userData.wheelRotation = ((car.userData.wheelRotation as number) || 0) + rotationDelta;
  const rotation = car.userData.wheelRotation as number;

  for (const wheel of wheels) {
    const rollAxis = (wheel.userData.rollAxis as 'x' | 'z') || 'x';
    
    if (rollAxis === 'x') {
      wheel.rotation.x = rotation;
    } else {
      wheel.rotation.z = rotation;
    }

    const name = wheel.name.toLowerCase();
    const isFront = name.includes('fl') || name.includes('fr') || 
                    name.includes('front') || name.includes('f_') ||
                    name.includes('lf') || name.includes('rf');
    
    if (isFront) {
      wheel.rotation.y = steer * 0.35;
    }
  }
}

export function updateCarLights(
  car: THREE.Group,
  opts: {
    throttle: number;
    steer: number;
    handbrake: boolean;
    fwdSpeed: number;
    speed: number;
    timeS: number;
  },
): void {
  const lights = car.userData.reactiveLights as ReactiveCarLights | undefined;
  if (!lights) return;

  const braking =
    (opts.throttle < -0.08 && opts.fwdSpeed > 0.5) ||
    (opts.handbrake && opts.speed > 8);
  const reversing = opts.fwdSpeed < -0.55 || opts.throttle < -0.2 && opts.speed < 3;
  const blink = Math.sin(opts.timeS * Math.PI * 3.4) > 0;
  const leftOn = opts.steer < -0.52 && opts.speed > 2.5 && blink;
  const rightOn = opts.steer > 0.52 && opts.speed > 2.5 && blink;

  for (const mat of lights.brake) mat.opacity = braking ? 0.95 : 0.22;
  for (const mat of lights.reverse) mat.opacity = reversing ? 0.82 : 0.08;
  for (const mat of lights.left) mat.opacity = leftOn ? 0.92 : 0.08;
  for (const mat of lights.right) mat.opacity = rightOn ? 0.92 : 0.08;
}
